<script setup lang="ts">
import { EVENT_TYPE, GAME_TYPE } from "~/constants";

export interface CardEventProps {
  title?: string;
  date?: string;
  imageUrl?: string;
  sportType?: TSportType;
  geniusId?: string;
  team1Name?: string;
  team2Name?: string;
  team1Score?: number;
  team2Score?: number;
  team1Color?: string;
  team2Color?: string;
  team1LogoUrl?: string;
  team2LogoUrl?: string;
  hasLock?: boolean;
  hasBookmark?: boolean;
  hasBookmarkDisabled?: boolean;
  hasTeamLogos?: boolean;
  hasVidswapStats?: boolean;
  hasDelete?: boolean;
  hasBadge?: boolean;
  hasDeleteDisabled?: boolean;
  commerceType: string;
  isBookmarkActive?: boolean;
  eventType?: string;
  modalCancelBtnTitle?: string;
  modalRemoveBtnTitle?: string;
  modalTitle?: string;
  badgeTitle?: string;
  bookmarkTooltipTitle?: string;
  gameType: TGameType;
  gender?: TGender | null;
}
const props = defineProps<CardEventProps>();
const emit = defineEmits(["clickBookmark", "clickDelete"]);

const hasStatistics = computed(() => Boolean(props.geniusId) || props.hasVidswapStats);
const isPracticeGameType = computed(() => props.gameType === GAME_TYPE.TRAINING || Boolean(props.team1Name && !props.team2Name));
const isOtherGameType = computed(() => props.gameType === GAME_TYPE.OTHER || Boolean(!props.team1Name && !props.team2Name));
const teamsTitle = computed(() => {
  if (isOtherGameType.value || isPracticeGameType.value)
    return props.title;

  if (props.team1Name && props.team2Name)
    return `${props.team1Name} vs ${props.team2Name}`;

  return null;
});

function onBookmarkClick() {
  emit("clickBookmark", { active: props.isBookmarkActive });
}

function onCardDelete() {
  emit("clickDelete");
}
</script>

<template>
  <div v-motion-pop class="cardEvent">
    <div class="cardEvent__imageWrap">
      <BaseImage class="cardEvent__image" :src="props.imageUrl" :alt="props.title" width="full" height="auto" />
      <div v-if="date" class="cardEvent__date text-caption-bold">
        <span class="text-white">{{ date }}</span>
      </div>
      <div v-if="props.badgeTitle && props.hasBadge" class="cardEvent__badge text-caption-medium flex items-center">
        <IconClip v-if="props.eventType === EVENT_TYPE.USER_CLIP" class="cardEvent__badge_icon" />
        <IconHighlight v-else-if="props.eventType === EVENT_TYPE.USER_HIGHLIGHT || props.eventType === EVENT_TYPE.HIGHLIGHT" class="cardEvent__badge_icon" />
        {{ props.badgeTitle }}
      </div>
      <div v-if="hasLock" class="cardEvent__lock text-caption-medium text-white flex gap-2">
        <IconLock class="z-10 flex-shrink-0" />

        <span class="z-10">{{ props.commerceType }}</span>
      </div>

      <BaseButton
        v-if="hasBookmark"
        v-tooltip="props.bookmarkTooltipTitle"
        class="cardEvent__bookmark"
        type="icon"
        name="add-bookmark"
        tag="div"
        :disabled="props.hasBookmarkDisabled"
        @click.prevent="onBookmarkClick()"
      >
        <IconBookmark class="z-10" :active="props.isBookmarkActive" />
      </BaseButton>

      <BaseButton
        v-if="hasDelete"
        class="cardEvent__bookmark"
        type="icon"
        name="delete-item"
        :disabled="props.hasDeleteDisabled"
        @click.prevent="onCardDelete"
      >
        <IconDelete class="z-10" fill="var(--base-error-color)" :width="18" :height="18" />
      </BaseButton>
    </div>

    <div class="cardEvent__content">
      <div class="flex-1">
        <div class="flex justify-between">
          <BlockTeamsInfo
            v-if="props.hasTeamLogos"
            class="py-2 h-16"
            :team1-name="props.team1Name"
            :team2-name="props.team2Name"
            :team1-color="props.team1Color"
            :team2-color="props.team2Color"
            :team1-logo-url="props.team1LogoUrl"
            :team2-logo-url="props.team2LogoUrl"
            :title="props.title"
            :game-type="props.gameType"
          />
          <p v-else class="text-body-medium text-main h-14 overflow-hidden">
            <span class="line-clamp-2">
              {{ teamsTitle }}
            </span>
          </p>
          <div v-if="props.team1Score && props.team2Score">
            <div
              class="cardEvent__scoreNumber"
              :class="{ cardEvent__scoreNumber_hight: Number(props.team1Score) > Number(props.team2Score) }"
            >
              {{ props.team1Score }}
            </div>
            <div
              class="cardEvent__scoreNumber"
              :class="{ cardEvent__scoreNumber_hight: Number(props.team1Score) < Number(props.team2Score) }"
            >
              {{ props.team2Score }}
            </div>
          </div>
        </div>

        <EventMetaInfo
          :title="props.title"
          :is-other-game-type="isOtherGameType"
          :is-practice-game-type="isPracticeGameType"
          :has-statistics="hasStatistics"
          :team-name="props.team1Name"
          :sport-type="props.sportType"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.cardEvent {
  @apply flex flex-col max-w-full
  overflow-hidden cursor-pointer
  transition duration-100;

  background-color: var(--base-card-bgColor);
  border-radius: var(--base-border-radius);

  &:hover {
    box-shadow: var(--base-box-shadow);
  }

  .cardEvent__content {
    @apply p-3 pt-4
    flex flex-col justify-between flex-1;
  }

  .cardEvent__imageWrap {
    @apply relative m-3 mb-0 flex-shrink-0 overflow-hidden;
  }

  .cardEvent__image {
    @apply w-full object-cover min-h-[8rem] max-h-44 aspect-[4/3];

    border-radius: var(--base-border-radius);
  }

  .cardEvent__date {
    @apply absolute bottom-0 left-0 p-2;

    color: var(--base-text-color);
    background-color: var(--base-primary-color);
    border-top-right-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  .cardEvent__badge {
    @apply absolute right-2 bottom-1.5 py-[2px] px-2;

    border-radius: 22px;
    background-color: var(--base-card-bgSecondaryColor);
    color: var(--base-text-color);
    .cardEvent__badge_icon {
      @apply mr-2;
    }
  }

  .cardEvent__bookmark {
    @apply absolute top-2 right-2
    w-10 h-10 rounded-full overflow-hidden
    flex justify-center items-center
    transition-transform duration-200 transform;

    color: var(--base-text-color);

    &:active {
      @apply scale-110;
    }

    &::before {
      @apply absolute w-full h-full z-0 opacity-[56%];

      content: '';
      background-color: #0f1624;
    }
  }

  .cardEvent__lock {
    @apply absolute top-1/2 left-1/2
    px-2 py-1 rounded-full overflow-hidden
    flex justify-center items-center;

    transform: translate(-50%, -50%);

    &::before {
      @apply absolute w-full h-full z-0 opacity-[56%];

      content: '';
      background-color: #0f1624;
    }
  }

  .cardEvent__scoreNumber {
    @apply relative text-base font-medium;

    color: var(--base-secondary-color);

    &.cardEvent__scoreNumber_hight {
      color: var(--base-text-color);

      &::before {
        position: absolute;
        bottom: 25%;
        left: -15px;
        content: '';
        border-top: 5px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 5px solid transparent;
        border-left: 8px solid var(--base-primary-accent-color, var(--base-primary-color));
      }
    }
  }
}
</style>
